import React, { useState } from 'react';
import { Table, Checkbox, Button, Card } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleTwoTone } from '@ant-design/icons';
import Box from '../../../../components/Box/Box';
import HeadingSubHeading from '../../../../components/ViewHeading/HeadingSubHeading';

interface DataType {
    key: string;
    event: string;
    employeeThemselves: boolean;
    reportingManager: boolean;
    l2Manager: boolean;
    departmentHead: boolean;
    everyone: boolean;
}

const EmployeeTimeline = () => {
    const [isEditMode, setIsEditMode] = useState(false);

    const [data, setData] = useState<DataType[]>([
        { key: '1', event: 'Employee Joined', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: true },
        { key: '2', event: 'Probation Completed', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '3', event: 'Probation End Date Changed', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '4', event: 'Work Anniversary', employeeThemselves: true, reportingManager: false, l2Manager: false, departmentHead: false, everyone: true },
        { key: '5', event: 'Employee Type Changed', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '6', event: 'Salary Revised', employeeThemselves: true, reportingManager: false, l2Manager: true, departmentHead: true, everyone: false },
        { key: '7', event: 'Job Title Changed', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '8', event: 'Reporting Manager Changed', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '9', event: 'Department Changed', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '10', event: 'Business Unit Changed', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '11', event: 'Location Changed', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '12', event: 'Added Direct Report', employeeThemselves: false, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '13', event: 'Employee Resigned Or Terminated', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '14', event: 'Last Working Day', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '15', event: 'Exit Request Revoked', employeeThemselves: true, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '16', event: 'Employee Rejoined', employeeThemselves: false, reportingManager: true, l2Manager: true, departmentHead: true, everyone: false },
        { key: '17', event: 'Legal Entity Changed', employeeThemselves: false, reportingManager: true, l2Manager: true, departmentHead: true, everyone: true },
    ]);

    const handleCheckboxChange = (key: string, column: keyof DataType, checked: boolean) => {
        setData((prevData) =>
            prevData.map((item) => (item.key === key ? { ...item, [column]: checked } : item))
        );
    };

    const handleEdit = (record, key) => {
        return isEditMode ? <Checkbox
            checked={record[key]}
            onChange={(e) => handleCheckboxChange(record.key, key, e.target.checked)}
        /> : record[key] ? <CheckCircleTwoTone style={{ fontSize: 20 }} /> : null;
    }

    const columns: ColumnsType<DataType> = [
        {
            title: 'Timeline Event',
            dataIndex: 'event',
            key: 'event',
            width: 250,
        },
        {
            title: 'Employee Themselves',
            dataIndex: 'employeeThemselves',
            key: 'employeeThemselves',
            render: (_, record) => (
                handleEdit(record, 'employeeThemselves')
            ),
            align: 'center',
        },
        {
            title: 'Reporting Manager',
            dataIndex: 'reportingManager',
            key: 'reportingManager',
            render: (_, record) => (
                handleEdit(record, 'reportingManager')
            ),
            align: 'center',
        },
        {
            title: 'L2 Manager',
            dataIndex: 'l2Manager',
            key: 'l2Manager',
            render: (_, record) => (
                handleEdit(record, 'l2Manager')
            ),
            align: 'center',
        },
        {
            title: 'Department & Head Above',
            dataIndex: 'departmentHead',
            key: 'departmentHead',
            render: (_, record) => (
                handleEdit(record, 'departmentHead')
            ),
            align: 'center',
        },
        {
            title: 'Everyone',
            dataIndex: 'everyone',
            key: 'everyone',
            render: (_, record) => (
                handleEdit(record, 'everyone')
            ),
            align: 'center',
        },
    ];

    return (
        <Card
            title={
                <HeadingSubHeading
                    titleProps={{ level: 5 }}
                    title='Employee Timeline'
                    subTitle='Select who should be notified when the following events occur'
                />
            }
            extra={<Button type="primary" onClick={() => setIsEditMode(!isEditMode)}>{isEditMode ? 'Save' : 'Edit'}</Button>}
        >
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                bordered
                scroll={{ y: 520 }}
                rowKey="key"
            />
        </Card>
    );
};

export default EmployeeTimeline;